import { Fragment } from "react";
import {
  type NameType,
  type ValueType,
} from "recharts/types/component/DefaultTooltipContent";
import LineChartModuleType from "shared/components/dashboard/LineChartModuleType";
import RingChartModuleType from "shared/components/dashboard/RingChartModuleType";
import ShortTableType from "shared/components/dashboard/ShortTableType";
import SimpleModuleType from "shared/components/dashboard/SimpleModuleType";
import StackedBarChartModuleType from "shared/components/dashboard/StackedBarChartModuleType";
import { useDashboardColumns } from "shared/components/dashboard/useDashboardColumns";
import { type SocietyDashboardModuleView } from "shared/mappers/database/dashboard/society-dashboard-module";

export type ChartDataType = Array<{ [key: NameType]: ValueType }>;

export const filterKeys = {
  start: "effective-from",
  end: "expiration-to",
};

export const DashboardModuleType = {
  SUM: "sum",
  TOTAL: "total",
  LINE_CHART: "lineChart",
  RING_GRAPH: "ringGraph",
  STACKED_BAR_CHART: "stackedBarChart",
  SHORT_TABLE: "shortTable",
  PIE_CHART: "pieChart",
};

export const dashboardMapper = {
  [DashboardModuleType.SUM]: (dashboardId: number) => (
    <SimpleModuleType dashboardId={dashboardId} />
  ),
  [DashboardModuleType.TOTAL]: (dashboardId: number) => (
    <SimpleModuleType dashboardId={dashboardId} />
  ),
  [DashboardModuleType.LINE_CHART]: (dashboardId: number) => (
    <LineChartModuleType dashboardId={dashboardId} />
  ),
  [DashboardModuleType.RING_GRAPH]: (dashboardId: number) => (
    <RingChartModuleType dashboardId={dashboardId} />
  ),
  [DashboardModuleType.STACKED_BAR_CHART]: (dashboardId: number) => (
    <StackedBarChartModuleType dashboardId={dashboardId} />
  ),
  [DashboardModuleType.SHORT_TABLE]: (dashboardId: number) => (
    <ShortTableType dashboardId={dashboardId} />
  ),
};

export const DashboardModules = ({
  dashboards,
}: {
  dashboards?: SocietyDashboardModuleView[];
}) => {
  const gridCols = useDashboardColumns({
    dashboards,
  });

  //need to sort dashboards by position row and column to make sure they are rendered in the correct order
  dashboards?.sort((a, b) => {
    if (a.positionRow === b.positionRow) {
      return (a.positionColumn ?? 1) - (b.positionColumn ?? 1);
    }
    return (a.positionRow ?? 1) - (b.positionRow ?? 1);
  });
  return (
    <div className={`grid grid-cols-${gridCols} gap-x-4 gap-y-1`}>
      {dashboards?.map((module) => {
        if (!module.type) {
          return <Fragment key={module.dashboardModuleId} />;
        }
        const pipe = dashboardMapper[module.type];

        return (
          <div
            key={module.dashboardModuleId}
            className={`w-full min-w-0 my-0.5 col-span-${
              module.colSpan
            } col-start-${module.positionColumn} 
            row-start-${module.positionRow} ${module.classNames}`}
          >
            {pipe(module.dashboardModuleId)}
          </div>
        );
      })}
    </div>
  );
};
